import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { EMPTY_ACTIVITY, editableFields } from "./leadDetailModel";

export const useLeadDetail = ({ isOpen, lead, userId, onLeadUpdated }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [showAddActivity, setShowAddActivity] = useState(false);
  const [newActivity, setNewActivity] = useState(EMPTY_ACTIVITY);
  const [addingActivity, setAddingActivity] = useState(false);

  const leadId = lead?.leadId;
  const fetchActivities = useCallback(async () => {
    if (!leadId) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/activities/lead/${leadId}`);
      setActivities(response.data);
    } catch (error) {
      console.error("Error fetching activities:", error);
      setActivities([]);
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => {
    if (isOpen && lead) {
      fetchActivities();
      setEditData(editableFields(lead));
    }
  }, [isOpen, lead, fetchActivities]);

  const setField = (field, value) => setEditData((prev) => ({ ...prev, [field]: value }));
  const setActivityField = (field, value) => setNewActivity((prev) => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/leads/${lead.leadId}`, editData);
      toast.success("Lead updated successfully");
      setIsEditing(false);
      onLeadUpdated?.({ ...lead, ...editData });
    } catch (error) {
      console.error("Error updating lead:", error);
      toast.error("Failed to update lead");
    } finally {
      setSaving(false);
    }
  };

  const handleAddActivity = async () => {
    if (!newActivity.subject.trim()) {
      toast.error("Please enter a subject");
      return;
    }
    setAddingActivity(true);
    try {
      const response = await axios.post(`${API}/activities`, { userId, leadId: lead.leadId, ...newActivity });
      setActivities((prev) => [response.data, ...prev]);
      setNewActivity(EMPTY_ACTIVITY);
      setShowAddActivity(false);
      toast.success("Activity logged");
      await axios.put(`${API}/leads/${lead.leadId}`, { lastActivity: new Date().toISOString() });
    } catch (error) {
      console.error("Error adding activity:", error);
      toast.error("Failed to log activity");
    } finally {
      setAddingActivity(false);
    }
  };

  return { activities, loading, isEditing, setIsEditing, editData, setField, saving, handleSave,
    showAddActivity, setShowAddActivity, newActivity, setActivityField, addingActivity, handleAddActivity };
};
